import { useCallback, useEffect, useRef, useState } from 'react'

type AudioSource = string | Blob | File

type PlaySoundOptions = {
  path: AudioSource
  onFinish?: () => void
  onPlay?: () => void
  onPause?: () => void
  onError?: (error: Error) => void
}

export type UsePlayerResult = {
  isPlaying: boolean
  playSound: (options: PlaySoundOptions) => Promise<void>
  stopSound: () => Promise<void>
  pauseSound: () => Promise<void>
}

export default function usePlayer(resetTimeOnStop = true): UsePlayerResult {
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const objectUrlRef = useRef<string | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)

  const releaseBlobUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
  }, [])

  const resolveSource = useCallback(
    (path: AudioSource) => {
      releaseBlobUrl()

      if (typeof path === 'string') {
        return path
      }

      const url = URL.createObjectURL(path)

      objectUrlRef.current = url

      return url
    },
    [releaseBlobUrl]
  )

  const attachListeners = useCallback((audio: HTMLAudioElement, callbacks: Omit<PlaySoundOptions, 'path'>) => {
    audio.onplay = () => {
      setIsPlaying(true)
      callbacks.onPlay?.()
    }

    audio.onpause = () => {
      setIsPlaying(false)
      callbacks.onPause?.()
    }

    audio.onended = () => {
      setIsPlaying(false)
      callbacks.onFinish?.()
    }

    audio.onerror = () => {
      setIsPlaying(false)

      callbacks.onError?.(new Error('Audio playback failed'))
    }
  }, [])

  const stopSound = useCallback(async () => {
    const audio = audioRef.current

    if (!audio) return

    audio.pause()

    if (resetTimeOnStop) {
      audio.currentTime = 0
    }

    setIsPlaying(false)
  }, [resetTimeOnStop])

  const pauseSound = useCallback(async () => {
    audioRef.current?.pause()

    setIsPlaying(false)
  }, [])

  const playSound = useCallback(
    async ({ path, onFinish, onPause, onPlay, onError }: PlaySoundOptions) => {
      try {
        await stopSound()

        const src = resolveSource(path)

        const audio = new Audio(src)

        audioRef.current = audio

        attachListeners(audio, {
          onFinish,
          onPause,
          onPlay,
          onError
        })

        await audio.play()
      } catch (error) {
        setIsPlaying(false)

        onError?.(error as Error)
      }
    },
    [attachListeners, resolveSource, stopSound]
  )

  useEffect(() => {
    return () => {
      audioRef.current?.pause()

      releaseBlobUrl()

      if (audioRef.current) {
        audioRef.current.src = ''
      }
    }
  }, [releaseBlobUrl])

  return {
    isPlaying,
    playSound,
    stopSound,
    pauseSound
  }
}
